import { QueueDashboardQueueType } from 'lib-dashboard-types';
import { useHasAccess } from 'lib-shared-access-control';
import { useEffect, useMemo, useState } from 'react';
import { useMount, useUnmount } from 'react-use';

import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';
import { useServerStorage } from 'utils/useServerStorage';

import { dashboardActions } from '../dashboard.actions';

type UseDashboardViewInitResult = {
  isInitialized: boolean;
};

/**
 * A hook that handles initializing the dashboard view based on URL params and
 * other conditions such as persisted user preferences.
 */
export const useDashboardViewInit = (): UseDashboardViewInitResult => {
  const { params } = useAppSearchParams('/dashboard/queues');
  const storage = useServerStorage('user');
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();

  const hasAnalyticsAccess = useHasAccess('analytics', {
    fallbackRole: 'teamlead',
  });

  const lastSearchParams = useAppSelector(
    (state) => state.dashboard.realTime?.lastSearchParams,
  );

  const [isInitialized, setIsInitialized] = useState(false);

  // Handles the case where the queue type in the URL search params is not found.
  // This can happen when accessing the page via a link to the root /dashboard path.
  // In this case, we redirect to the last persisted search params if available, otherwise to the
  // user's preferred queue type, or the fallback queue type based on user role.
  useMount(() => {
    if (params.type) return;

    if (lastSearchParams && Object.keys(lastSearchParams).length) {
      navigate(
        {
          path: '/dashboard/queues',
          params: {
            search: lastSearchParams,
          },
        },
        { replace: true },
      );
    } else {
      const fallbackQueueType: QueueDashboardQueueType = hasAnalyticsAccess
        ? 'all'
        : 'agent';

      const defaultQueueType =
        storage.data.dashboard?.defaultQueueType ??
        (window.localStorage.getItem(
          'selected-queue-type',
        ) as QueueDashboardQueueType) ??
        fallbackQueueType;

      navigate(
        {
          path: '/dashboard/queues',
          params: {
            search: {
              type: defaultQueueType,
            },
          },
        },
        { replace: true },
      );
    }
  });

  // Updates the init status when a supported `type` URL search param is found.
  useEffect(() => {
    if (params.type && !isInitialized) {
      setIsInitialized(true);
    }
    // Only run this effect when the URL param changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.type]);

  useUnmount(() => {
    dispatch(dashboardActions.setLastSearchParams({ params }));
  });

  return useMemo(() => ({ isInitialized }), [isInitialized]);
};
